import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    Box, Typography, Button, CircularProgress,
    Dialog, DialogContent, DialogActions,
} from '@mui/material';
import FolderSharedOutlinedIcon from '@material-ui/icons/FolderSharedOutlined';
import TimelineIcon from '@material-ui/icons/Timeline';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import CheckIcon from '@material-ui/icons/Check';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import { getConsentSetupService, confirmConsentPathwayService } from '../../services/client.service';
import {
    CONSENT_STAGES, CONSENT_REQUIREMENTS,
    getConsentPathway, pathwayFromAnswers, answersFromPathway, lowerTitle,
} from './components/consentPathways.config';
import { TOAST_TYPES } from '../../utils/constants';
import { toast } from '../../utils/utils';
import { useColorMode } from '../../context/colorMode/colorMode.context';
import { DARK_TINT_VARS } from '../../utils/buildTheme';

/**
 * Parental Consent — a school or network setting up how consent is collected
 * for its learners. SNW, SW_Parental Consent Flow (5 Oct 2026), slides 8-19
 * (first-time set-up) and 27-35 (a school confirming its network's method).
 *
 * Three steps: Overview → Choose method → Review and confirm. The method can be
 * confirmed once and never changed, so a workspace that has already confirmed
 * opens straight on the Review step, read-only, with a "Back to Home" button.
 *
 * A school inside a network sees its network's method pre-selected ("Set by your
 * network") and may keep it or choose another. Either way the school confirms
 * for itself — its classes cannot be published until it has (the API refuses).
 *
 * `?from=support` sends Back/finish to Support instead of Home.
 */

const ORANGE_SOFT = 'var(--tint-soft-2, #FFF8F1)';
const TEXT = 'var(--brand-text, #1F2328)';
const MUTED = 'var(--brand-text-muted, #4A5059)';
const BORDER = 'var(--brand-border, #E4E6EA)';
const WARM_BORDER = 'var(--tint-warm-border, #F1E0CE)';

const STEPS = ['Overview', 'Choose method', 'Review and confirm'];

const primaryBtnSx = (enabled = true) => ({
    backgroundColor: enabled ? '#FF6900' : '#F5B98E',
    color: '#fff !important',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '15px',
    px: 3.5,
    py: 1.1,
    borderRadius: '8px',
    boxShadow: 'none',
    cursor: enabled ? 'pointer' : 'default',
    '&:hover': { backgroundColor: enabled ? '#e55a00' : '#F5B98E', boxShadow: 'none' },
});

const outlineBtnSx = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '15px',
    color: TEXT,
    border: `1px solid ${BORDER}`,
    backgroundColor: 'var(--brand-surface, #fff)',
    px: 3,
    py: 1.05,
    borderRadius: '8px',
};

const Chip = ({ children, filled = false }) => (
    <Box component="span" sx={{
        display: 'inline-block',
        fontSize: '12.5px',
        fontWeight: 600,
        borderRadius: '999px',
        px: 1.25,
        py: '2px',
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        ...(filled
            ? { backgroundColor: '#FF6900', color: '#fff' }
            : { border: '1px solid var(--tint-accent-border, #F8B98A)', color: 'var(--tint-accent-text, #B84E08)', backgroundColor: 'var(--tint-chip-bg, #FFF4EA)' }),
    }}>
        {children}
    </Box>
);

// ─── Progress bar ────────────────────────────────────────────────────────────

const StepBar = ({ step, onPick }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 4 }}>
        {STEPS.map((label, i) => {
            const reached = i <= step;
            const done = i < step;
            return (
                <Box
                    key={label}
                    onClick={() => onPick && onPick(i)}
                    sx={{ cursor: onPick ? 'pointer' : 'default', minWidth: 0 }}
                >
                    <Box sx={{ height: 4, borderRadius: 2, backgroundColor: reached ? '#FF6900' : 'var(--tint-track, #EDE3D8)', mb: 1.25 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '13px', fontWeight: 700,
                            backgroundColor: reached ? '#FF6900' : 'var(--tint-track, #EDE3D8)',
                            color: reached ? '#fff' : '#8A8F98',
                        }}>
                            {done ? <CheckIcon style={{ fontSize: 16 }} /> : i + 1}
                        </Box>
                        <Typography sx={{
                            fontSize: '15px',
                            fontWeight: i === step ? 700 : 400,
                            color: reached ? TEXT : '#8A8F98',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                            {label}
                        </Typography>
                    </Box>
                </Box>
            );
        })}
    </Box>
);

// ─── Step 1 — Overview ───────────────────────────────────────────────────────

const OVERVIEW_CARDS = [
    {
        icon: FolderSharedOutlinedIcon,
        title: 'What is collected',
        text: 'Powered by DQ collects each learner’s name, age, email and location (city, state, country), along with their assessment responses and learning progress.',
    },
    {
        icon: TimelineIcon,
        title: 'How it is used',
        text: 'Powered by DQ uses learners\' data to report assessment results and learning impact through scorecards and dashboards.',
    },
    {
        icon: MailOutlineIcon,
        title: 'Who collects consent',
        text: 'Your school collects consent with a signed Parental Consent Form, or Powered by DQ collects it by emailing parents when the class is published.',
    },
];

const OverviewStep = () => (
    <>
        <Typography component="h1" sx={{ fontSize: { xs: '26px', sm: '32px' }, fontWeight: 700, color: TEXT, mb: 1.5 }}>
            Parental consent is required for every learner
        </Typography>
        <Typography sx={{ fontSize: '17px', color: MUTED, lineHeight: 1.65, mb: 4, maxWidth: 920 }}>
            Each learner gets a personal learning space for assessments and learning programs. Because this
            involves a child&apos;s personal data, GDPR, COPPA, and equivalent data protection laws require a
            parent&apos;s consent. Choose how your school will collect it.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
            {OVERVIEW_CARDS.map(({ icon: Icon, title, text }) => (
                <Box key={title} sx={{ border: `1px solid ${WARM_BORDER}`, borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', p: 3 }}>
                    <Icon style={{ fontSize: 30, color: '#FF6900' }} />
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: TEXT, mt: 1.5, mb: 1 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.65 }}>{text}</Typography>
                </Box>
            ))}
        </Box>
    </>
);

// ─── Step 2 — Choose method ──────────────────────────────────────────────────

const Option = ({ on, label, recommended, onClick, disabled }) => (
    <Box
        role="radio"
        aria-checked={on}
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : onClick}
        onKeyDown={(e) => {
            if (!disabled && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); onClick(); }
        }}
        sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            border: on ? '2px solid #FF6900' : `1px solid ${BORDER}`,
            backgroundColor: on ? ORANGE_SOFT : 'var(--brand-surface, #fff)',
            borderRadius: '10px',
            px: 2.5, py: on ? '15px' : '16px',
            cursor: disabled ? 'default' : 'pointer',
        }}
    >
        <Box sx={{
            width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
            border: `2px solid ${on ? '#FF6900' : '#C9CDD2'}`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: on ? '#FF6900' : 'transparent' }} />
        </Box>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: TEXT }}>{label}</Typography>
        {recommended && <Chip filled>Recommended</Chip>}
    </Box>
);

const Question = ({ n, title, children }) => (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', p: { xs: 2, sm: 3 }, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2 }}>
            <Typography sx={{ color: '#FF6900', fontWeight: 700, fontSize: '16px' }}>{n}</Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: TEXT }}>{title}</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
            {children}
        </Box>
    </Box>
);

const NetworkBanner = ({ networkName, assigned }) => (
    <Box sx={{ border: `1px solid ${WARM_BORDER}`, borderRadius: '10px', backgroundColor: 'var(--brand-surface, #fff)', px: 2.5, py: 2, mb: 4 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: TEXT, mb: 0.5 }}>Set by your network</Typography>
        <Typography sx={{ fontSize: '15.5px', color: MUTED }}>
            {networkName} has set {lowerTitle(assigned)} as your school&rsquo;s method. You can keep it or change it before you confirm.
        </Typography>
    </Box>
);

const ChooseStep = ({ q1, q2, setQ1, setQ2, selected, assigned, networkName }) => (
    <>
        {assigned && <NetworkBanner networkName={networkName} assigned={assigned} />}
        <Typography component="h1" sx={{ fontSize: { xs: '26px', sm: '32px' }, fontWeight: 700, color: TEXT, mb: 1 }}>
            Choose how consent is collected
        </Typography>
        <Typography sx={{ fontSize: '17px', color: MUTED, mb: 3 }}>
            Answer a question or two to find what fits your school.
        </Typography>

        <Question n={1} title="Will your school collect parental consent itself?">
            <Option on={q1 === 'school'} recommended label="Yes, the school collects consent." onClick={() => { setQ1('school'); setQ2(null); }} />
            <Option on={q1 === 'dq'} label="No, Powered by DQ collects consent." onClick={() => setQ1('dq')} />
        </Question>

        {q1 === 'dq' && (
            <Question n={2} title="Do your learners have their own email addresses?">
                <Option on={q2 === 'yes'} label="Yes, learners have email." onClick={() => setQ2('yes')} />
                <Option on={q2 === 'no'} label="No, learners don't have email." onClick={() => setQ2('no')} />
            </Question>
        )}

        {selected && (
            <Box sx={{ display: 'flex', gap: 1.5, backgroundColor: 'var(--tint-soft, #FFF1E4)', borderRadius: '12px', px: 2.5, py: 2.25 }}>
                <ArrowForwardIcon style={{ color: '#FF6900', fontSize: 20, marginTop: 3, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: TEXT }}>
                            Your method: {selected.title}
                        </Typography>
                        {assigned && assigned.id === selected.id && <Chip>Set by your network</Chip>}
                    </Box>
                    <Typography sx={{ fontSize: '15.5px', color: MUTED }}>{selected.summary}</Typography>
                    {assigned && assigned.id !== selected.id && (
                        <Typography sx={{ fontSize: '14.5px', color: 'var(--tint-accent-text, #B84E08)', mt: 1 }}>
                            Your network chose {lowerTitle(assigned)}. You&rsquo;ve chosen a different method.
                        </Typography>
                    )}
                </Box>
            </Box>
        )}
    </>
);

// ─── Step 3 — Review and confirm ─────────────────────────────────────────────

const ReviewStep = ({ pathway, fromNetwork }) => (
    <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Typography component="h1" sx={{ fontSize: { xs: '26px', sm: '32px' }, fontWeight: 700, color: TEXT }}>
                {pathway.title}
            </Typography>
            {pathway.recommended && <Chip filled>Recommended</Chip>}
            {fromNetwork && <Chip>Set by your network</Chip>}
        </Box>
        <Typography sx={{ fontSize: '17px', color: MUTED, mb: 3.5 }}>{pathway.summary}</Typography>

        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: TEXT, mb: 1.5 }}>Requirements</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 3 }}>
            {CONSENT_REQUIREMENTS.map((label, i) => {
                const on = !!pathway.needs[i];
                const Icon = on ? CheckCircleOutlineIcon : RemoveCircleOutlineIcon;
                return (
                    <Box key={label} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        border: `1px solid ${on ? 'var(--tint-accent-border, #F8B98A)' : 'var(--tint-off-border, #ECEEF0)'}`,
                        backgroundColor: on ? 'var(--tint-soft, #FFF1E4)' : 'var(--tint-off-bg, #F6F7F8)',
                        borderRadius: '10px', px: 2, py: 1.25,
                    }}>
                        <Icon style={{ color: on ? '#FF6900' : '#C9CDD2', fontSize: 22 }} />
                        <Box>
                            <Typography sx={{ fontSize: '15.5px', fontWeight: 700, color: on ? TEXT : '#9AA0A8' }}>{label}</Typography>
                            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: on ? 'var(--tint-accent-text, #B84E08)' : '#9AA0A8' }}>
                                {on ? 'Required' : 'Not needed'}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: { xs: 1.5, md: 1 }, mb: 3 }}>
            {pathway.stages.map((items, i) => (
                <React.Fragment key={CONSENT_STAGES[i]}>
                    {i > 0 && (
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', color: '#C9CDD2' }}>
                            <ArrowForwardIcon style={{ fontSize: 20 }} />
                        </Box>
                    )}
                    <Box sx={{ flex: 1, border: `1px solid ${WARM_BORDER}`, borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', p: 2.5 }}>
                        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: TEXT, mb: 1.25 }}>{CONSENT_STAGES[i]}</Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li::marker': { color: '#FF6900' } }}>
                            {items.map((t) => (
                                <Typography component="li" key={t} sx={{ fontSize: '15.5px', color: TEXT, lineHeight: 1.6, mb: 0.75 }}>{t}</Typography>
                            ))}
                        </Box>
                    </Box>
                </React.Fragment>
            ))}
        </Box>

        {pathway.deadline && (
            <Box sx={{ display: 'flex', gap: 1.5, border: '1px solid var(--tint-warn-border, #F5C6A5)', backgroundColor: 'var(--tint-warn-bg, #FFF6EE)', borderRadius: '12px', px: 2.5, py: 2, mb: 3 }}>
                <ReportProblemOutlinedIcon style={{ color: '#D9480F', fontSize: 22, flexShrink: 0, marginTop: 1 }} />
                <Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: TEXT, mb: 0.5 }}>{pathway.cautionTitle}</Typography>
                    <Typography sx={{ fontSize: '15px', color: MUTED }}>{pathway.deadline}</Typography>
                </Box>
            </Box>
        )}

        {pathway.documents.length > 0 && (
            <Box sx={{ border: `1px solid ${WARM_BORDER}`, borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 3, py: 2, borderBottom: `1px solid ${WARM_BORDER}` }}>
                    <DescriptionOutlinedIcon style={{ color: '#FF6900', fontSize: 22 }} />
                    <Typography sx={{ fontSize: '17px', fontWeight: 700, color: TEXT }}>Documents</Typography>
                </Box>
                {pathway.documents.map((d, i) => (
                    <Box key={d.name} sx={{
                        display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between', gap: 2, px: 3, py: 2,
                        borderTop: i ? '1px solid var(--tint-divider, #F4ECE3)' : 0,
                    }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: TEXT }}>{d.name}</Typography>
                                {d.optional && (
                                    <Box component="span" sx={{ fontSize: '12.5px', color: MUTED, backgroundColor: 'var(--tint-tag-bg, #F1F2F4)', borderRadius: '999px', px: 1.25, py: '1px' }}>
                                        Optional
                                    </Box>
                                )}
                            </Box>
                            <Typography sx={{ fontSize: '15px', color: MUTED }}>{d.desc}</Typography>
                        </Box>
                        <Button
                            href={d.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<GetAppIcon />}
                            sx={{ ...primaryBtnSx(true), flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                            Download
                        </Button>
                    </Box>
                ))}
            </Box>
        )}
    </>
);

// ─── Page ────────────────────────────────────────────────────────────────────

const ParentalConsent = () => {
    const { mode } = useColorMode();
    const history = useHistory();
    const location = useLocation();
    const fromSupport = new URLSearchParams(location.search).get('from') === 'support';
    const exitPath = fromSupport ? '/support' : '/my_workspace';

    const [setup, setSetup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(0);
    const [q1, setQ1] = useState(null);
    const [q2, setQ2] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // A school inheriting its network's method chose to switch to its own.
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        let alive = true;
        getConsentSetupService()
            .then((res) => {
                if (!alive) return;
                const data = res?.data || null;
                setSetup(data);
                // A method that applies (confirmed, or the network's) opens on
                // Review; the network's method pre-fills the answers.
                const initial = data?.consent_pathway || data?.assigned_pathway || null;
                const a = answersFromPathway(initial);
                setQ1(a.q1);
                setQ2(a.q2);
                if (data?.consent_pathway) setStep(2);
            })
            .catch(() => { /* invokeApi surfaces the error */ })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    /*
     * Three states. `own`: this school or network confirmed a method — read-only
     * for good. `inherited`: the school has none of its own, so its network's
     * applies (13 Oct 2026) — read-only too, but the school may switch to its own
     * ("Change method") until one of its classes is published (`can_change`).
     * Otherwise nothing applies yet and the wizard runs as normal.
     */
    const ownId = setup?.own_pathway !== undefined ? setup.own_pathway : setup?.consent_pathway;
    const own = getConsentPathway(ownId);
    const inherited = !own && setup?.inherited ? getConsentPathway(setup.consent_pathway) : null;
    const canChange = !!inherited && setup?.can_change === true;
    const confirmed = own || (inherited && !editing ? inherited : null);
    const assigned = own ? null : getConsentPathway(setup?.assigned_pathway);
    const selected = confirmed || getConsentPathway(pathwayFromAnswers(q1, q2));
    const networkName = setup?.network?.org_name || 'Your network';
    const workspaceName = setup?.org_name || 'your school';
    const backHome = fromSupport ? 'Back to Support' : 'Back to Home';

    const footer = useMemo(() => {
        if (step === 0) return { label: 'Continue', ok: true, hint: '' };
        if (step === 1) return { label: 'Review method', ok: !!selected, hint: selected ? '' : 'Answer both questions to continue.' };
        if (own) return { label: backHome, ok: true, hint: 'Confirmed. This method can’t be changed.' };
        if (confirmed) {
            return {
                label: backHome,
                ok: true,
                hint: canChange
                    ? `Set by ${networkName}. It applies to your school.`
                    : `Set by ${networkName}. Your classes are published under it, so it can’t be changed.`,
            };
        }
        return { label: 'Confirm method', ok: !!selected, hint: 'Once confirmed, the method can’t be changed.' };
    }, [step, selected, own, confirmed, canChange, networkName, backHome]);

    const next = () => {
        if (!footer.ok) return;
        if (step < 2) { setStep(step + 1); return; }
        if (confirmed) history.push(exitPath);
        else setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            const res = await confirmConsentPathwayService(selected.id);
            if (res?.data?.consent_pathway) {
                setConfirmOpen(false);
                toast(TOAST_TYPES.SUCCESS, 'Parental Consent', 'Parental consent method confirmed.');
                history.push(exitPath);
            }
        } catch (e) {
            // invokeApi surfaces the backend message as a toast.
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
                <CircularProgress sx={{ color: '#FF6900' }} />
            </Box>
        );
    }

    if (!setup) {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', py: 10, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '18px', color: MUTED, mb: 3 }}>
                    Parental consent could not be loaded. Please try again.
                </Typography>
                <Button onClick={() => history.push(exitPath)} sx={outlineBtnSx}>
                    {fromSupport ? 'Back to Support' : 'Back to Home'}
                </Button>
            </Box>
        );
    }

    // Steps are clickable only while choosing — never after confirmation, and
    // never forward to Review without a method.
    const pickStep = confirmed ? null : (i) => { if (i < 2 || selected) setStep(i); };

    const startChange = () => { setEditing(true); setStep(1); };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 120px)', ...(mode === 'dark' ? DARK_TINT_VARS : {}) }}>
            <Box sx={{ flex: 1, width: '100%', maxWidth: 1180, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
                <StepBar step={step} onPick={pickStep} />
                {step === 0 && <OverviewStep />}
                {step === 1 && (
                    <ChooseStep
                        q1={q1} q2={q2} setQ1={setQ1} setQ2={setQ2}
                        selected={selected} assigned={assigned} networkName={networkName}
                    />
                )}
                {step === 2 && selected && (
                    <ReviewStep pathway={selected} fromNetwork={!!assigned && assigned.id === selected.id} />
                )}
            </Box>

            <Box sx={{ borderTop: `1px solid ${BORDER}`, backgroundColor: 'var(--brand-surface, #fff)', position: 'sticky', bottom: 0 }}>
                <Box sx={{
                    maxWidth: 1180, mx: 'auto', px: { xs: 2, sm: 3 }, py: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
                }}>
                    <Box>
                        {step > 0 && !confirmed && (
                            <Button onClick={() => setStep(step - 1)} sx={outlineBtnSx}>Back</Button>
                        )}
                        {confirmed && !own && canChange && (
                            <Button onClick={startChange} sx={outlineBtnSx}>Change method</Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {footer.hint && <Typography sx={{ fontSize: '15px', color: MUTED }}>{footer.hint}</Typography>}
                        <Button onClick={next} sx={primaryBtnSx(footer.ok)}>{footer.label}</Button>
                    </Box>
                </Box>
            </Box>

            <Dialog
                open={confirmOpen && !!selected}
                onClose={saving ? undefined : () => setConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogContent sx={{ pt: 3.5, px: 3.5 }}>
                    <Typography sx={{ fontSize: '22px', fontWeight: 700, color: TEXT, mb: 2 }}>
                        Confirm {lowerTitle(selected)}?
                    </Typography>
                    <Typography sx={{ fontSize: '16px', color: MUTED, lineHeight: 1.6 }}>
                        {selected ? selected.title.charAt(0) + selected.title.slice(1).toLowerCase() : ''} will apply
                        to all classes in {workspaceName}. Once confirmed, the method can&rsquo;t be changed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3.5, pb: 3, pt: 2, gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} disabled={saving} sx={outlineBtnSx}>Cancel</Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
                        sx={primaryBtnSx(!saving)}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ParentalConsent;
